import { useForm } from "react-hook-form";
import { IMsFormFilter } from "../types";
import Grid from "@mui/material/Grid";
import { FormAutocomplete } from "../../common/form-fields-wrappers";
import { useTranslation } from "react-i18next";
import { MS_FILTER_DEFAULTS } from "./const";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import { useContext, useEffect, useMemo, useState } from "react";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { useQuery } from "@tanstack/react-query";
import { getMobilityStores, getMsFilters } from "../services/http.services";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import { ReplayOutlined, SearchRounded } from "@mui/icons-material";
import Stack from "@mui/material/Stack";
import { useRecoilState } from "recoil";
import { mobilityStoresFiltersState, mobilityStoresState } from "../state";
import * as yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup";
import { deepClone } from "../../../../util/state/commonFunctions";
import CustomLoader from "../../../../util/component/CustomLoader";

function MsFilter() {
    const theme = useTheme();
    const {t} = useTranslation(['network', 'translation']);
    const {t: t2} = useTranslation(['translation']);
    const { globalData } = useContext(GlobalContext);

    const [filters, setFilters] = useRecoilState(mobilityStoresFiltersState);
    const [rowData, setRowData] = useRecoilState(mobilityStoresState);

    const [disableCounty, setDisableCountry] = useState(false);

    const tenants = useMemo<string[]>(() => {
        return globalData?.user.legalEntities?.map((elem: any) => elem.name) || [];
    }, [globalData]);

    const validationSchema = yup.object({
        countries: yup
            .object()
            .required(t2("error.required"))
            .typeError(t2("error.required"))
    });

    const form = useForm<IMsFormFilter>({
        defaultValues: MS_FILTER_DEFAULTS,
        resolver: yupResolver(validationSchema),
    });

    const filtersOptionsQuery = useQuery({
        queryKey: ["getMsFilters", tenants],
        queryFn: () => getMsFilters({ tenants }),
    });

    const formCountries = form.watch("countries") as any;
    const filterOptions = useMemo<{ [key in keyof IMsFormFilter]: any[] }>(() => {
        const transform = (value: any) => ({
            ...value,
            value: value.id,
            label: translateType(value.name),
        });

        const options = {
            countries: filtersOptionsQuery.data?.countries?.map(transform) || [],
            regions: filtersOptionsQuery.data?.regions?.map(transform) || [],
            cities: filtersOptionsQuery.data?.cities?.map(transform) || [],
            types: filtersOptionsQuery.data?.types?.map(transform) || [],
            clusters: filtersOptionsQuery.data?.clusters?.map(transform) || [],
        };

        if (!formCountries) {
            return options;
        }

        options.regions = options.regions.filter((region: any) =>
            Array.isArray(formCountries)
                ? formCountries.find((fc: any) => fc.name === region.countryId)
                : formCountries.name === region.countryId 
        );

        options.cities = options.cities.filter((city: any) =>
            Array.isArray(formCountries)
                ? formCountries.find((fc: any) => fc.name === city.countryId)
                : formCountries.name === city.countryId 
        );

        return options;
    }, [filtersOptionsQuery.data, t, formCountries]);

    useEffect(() => {
        handleReset();
    }, [tenants, filtersOptionsQuery.data]);

    function handleReset() {
        form.reset();

        if (filterOptions?.countries?.length && tenants.length === 1) {
            const defaultCountry = filterOptions.countries.find(({ tenant }) => tenant === tenants[0]);
            form.setValue("countries", defaultCountry);
            setDisableCountry(true);
        } else {
            setDisableCountry(false);
        }
    }

    function handleSearch(data: any) {
        const dataClone = deepClone(data);
        dataClone.countries = [dataClone.countries];
        setFilters(dataClone);
    }

    function translateType(type: string) {
        if (type === "Mobility Store") {
            return t("msHoliday.type.mobility_store");
        } else if (type === "Repair Shop") {
            return t("msHoliday.type.repair_shop");
        }

        return type;
    }

    return (
        <>
            <Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>
                <Grid container columnSpacing={2}>
                    <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                        <FormAutocomplete<IMsFormFilter>
                            name='countries'
                            control={form.control}
                            label={t("msHoliday.fields.country")}
                            fullWidth
                            options={filterOptions.countries}
                            disabled={disableCounty}
                            required
                        />
                    </Grid>
                    <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                        <FormAutocomplete<IMsFormFilter>
                            name='regions'
                            control={form.control}
                            label={t("msHoliday.fields.region")}
                            fullWidth
                            multiple
                            options={filterOptions.regions}
                        />
                    </Grid>
                    <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                        <FormAutocomplete<IMsFormFilter>
                            name='cities'
                            control={form.control}
                            label={t("msHoliday.fields.city")}
                            fullWidth
                            multiple
                            options={filterOptions.cities}
                        />
                    </Grid>
                    <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                        <FormAutocomplete<IMsFormFilter>
                            name='types'
                            control={form.control}
                            label={t("msHoliday.fields.type")}
                            fullWidth
                            multiple
                            options={filterOptions.types}
                        />
                    </Grid>
                    <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                        <FormAutocomplete<IMsFormFilter>
                            name='clusters'
                            control={form.control}
                            label={t("msHoliday.fields.cluster")}
                            fullWidth
                            multiple
                            options={filterOptions.clusters}
                        />
                    </Grid>
                </Grid>
                <Stack direction="row" alignItems="center" justifyContent="end" columnGap={2}>
                    <Button
                        variant="text"
                        color="accent"
                        startIcon={<ReplayOutlined />}
                        onClick={handleReset}
                    >
                        {t("msHoliday.labels.reset")}
                    </Button>
                    <Tooltip title={t('msHoliday.labels.search')}>
                        <Button
                            size="icon"
                            color="accent"
                            onClick={form.handleSubmit(handleSearch)}
                        >
                            <SearchRounded/>
                        </Button>
                    </Tooltip>
                </Stack>
            </Paper>
            <CustomLoader loading={filtersOptionsQuery.isLoading} />
        </>
    )
}

export default MsFilter;